import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { UnifiedSubscription, Platform, SubscriptionStatus } from '../types/subscription';
import { convertTimestamp } from './firestoreService';
import { Timestamp } from 'firebase/firestore';

interface FirestoreSubscription {
  userId: string;
  userEmail?: string;
  platform: string;
  status: string;
  planId: string;
  amount?: number;
  currency?: string;
  currentPeriodStart?: Timestamp | Date | string;
  currentPeriodEnd?: Timestamp | Date | string;
  isTrial?: boolean;
  trialEndDate?: Timestamp | Date | string;
  cancelAtPeriodEnd?: boolean;
  stripeSubscriptionId?: string;
  purchaseId?: string;
  productId?: string;
  environment?: 'test' | 'production';
  createdAt?: Timestamp | Date | string;
  updatedAt?: Timestamp | Date | string;
}

export const getAllSubscriptions = async (): Promise<UnifiedSubscription[]> => {
  try {
    const subscriptionsRef = collection(db, 'user_subscriptions');
    const snapshot = await getDocs(subscriptionsRef);

    const subscriptions: UnifiedSubscription[] = [];

    for (const doc of snapshot.docs) {
      const data = doc.data() as FirestoreSubscription;
      
      // Skip if missing required fields
      if (!data.userId || !data.platform || !data.status) {
        continue;
      }

      const platform = data.platform.toLowerCase() as Platform;
      if (!['stripe', 'ios', 'android'].includes(platform)) {
        continue;
      }

      const subscription: UnifiedSubscription = {
        userId: data.userId,
        email: data.userEmail || '',
        platform,
        status: data.status as SubscriptionStatus,
        planId: data.planId || 'unknown',
        planName: data.planId,
        amount: data.amount || 0,
        currency: data.currency || 'usd',
        currentPeriodStart: data.currentPeriodStart 
          ? convertTimestamp(data.currentPeriodStart)
          : new Date(),
        currentPeriodEnd: data.currentPeriodEnd
          ? convertTimestamp(data.currentPeriodEnd)
          : new Date(),
        isTrial: data.isTrial || false,
        trialEndDate: data.trialEndDate
          ? convertTimestamp(data.trialEndDate)
          : undefined,
        cancelAtPeriodEnd: data.cancelAtPeriodEnd || false,
        stripeSubscriptionId: data.stripeSubscriptionId,
        mobilePurchaseId: data.purchaseId,
        productId: data.productId,
        environment: data.environment || 'production', // Default to production if not set
        createdAt: data.createdAt
          ? convertTimestamp(data.createdAt)
          : new Date(),
        updatedAt: data.updatedAt
          ? convertTimestamp(data.updatedAt)
          : new Date(),
      };

      subscriptions.push(subscription);
    }

    return subscriptions;
  } catch (error) {
    console.error('Error fetching subscriptions:', error);
    throw error;
  }
};

export const getSubscriptionsByPlatform = async (
  platform: Platform
): Promise<UnifiedSubscription[]> => {
  const allSubscriptions = await getAllSubscriptions();
  return allSubscriptions.filter((sub) => sub.platform === platform);
};

export const getSubscriptionsByStatus = async (
  status: SubscriptionStatus
): Promise<UnifiedSubscription[]> => {
  const allSubscriptions = await getAllSubscriptions();
  return allSubscriptions.filter((sub) => sub.status === status);
};

export const getSubscriptionsByEmail = async (
  email: string
): Promise<UnifiedSubscription[]> => {
  const allSubscriptions = await getAllSubscriptions();
  const normalizedEmail = email.toLowerCase().trim();
  return allSubscriptions.filter(
    (sub) => sub.email.toLowerCase().trim() === normalizedEmail
  );
};

